import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { EntityResources, UnitOfWork } from "../api/public-api";

@Injectable()
export class BankingTraderUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'bankingTrader' }), httpClient);
    }

    account(bankBIC: string, clientUserId: string, resourceId: string, dateFrom: Date, dateTo: Date): Promise<any> {
        const url = `${this.baseUrl}api/bankingTrader/account`;

        const params = new HttpParams()
            .set('bankBIC', bankBIC)
            .set('clientUserId', clientUserId)
            .set('resourceId', resourceId)
            .set('dateFrom', dateFrom.toDateString())
            .set('dateTo', dateTo.toDateString());

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }

}

@Injectable()
export class TraderBankAccountUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'traderBankAccount' }), httpClient);
    }

    hasConnection(id: number): Promise<any> {
        const url = `${this.baseUrl}api/traderBankAccount/hasConnection`;

        const params = new HttpParams().set('id', id);

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }
}

@Injectable()
export class NbgSupportedBankUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'nbgSupportedBank' }), httpClient);
    }

}

@Injectable()
export class NbgConnectionUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'nbgConnection' }), httpClient);
    }

    add(traderBankAccountId: number): Promise<any> {
        const url = `${this.baseUrl}api/nbgConnection/add`;

        const params = new HttpParams().set('traderBankAccountId', traderBankAccountId);

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }

    removeConnection(traderBankAccountId: number): Promise<any> {
        const url = `${this.baseUrl}api/nbgConnection/delete`;

        const params = new HttpParams().set('traderBankAccountId', traderBankAccountId);

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }

    details(traderBankAccountId: number): Promise<any> {
        const url = `${this.baseUrl}api/nbgConnection/details`;

        const params = new HttpParams().set('traderBankAccountId', traderBankAccountId);

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }

    config(): Promise<any> {
        const url = `${this.baseUrl}api/nbgConnection/config`;

        return this.httpClient.get(url).toPromise();
    }
}

@Injectable()
export class NbgAccountUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'nbgAccount' }), httpClient);
    }

    details(traderBankAccountId: number, resourceId: string): Promise<any> {
        const url = `${this.baseUrl}api/nbgAccount/details`;

        const params = new HttpParams()
            .set('traderBankAccountId', traderBankAccountId)
            .set('resourceId', resourceId);

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }

    transactions(traderBankAccountId: number, resourceId: string, iban: string, dateFrom: string, dateTo: string): Promise<Blob> {
        const url = `${this.baseUrl}api/nbgAccount/transactions`;

        const params = new HttpParams()
            .set('traderBankAccountId', traderBankAccountId)
            .set('resourceId', resourceId)
            .set('iban', iban || '')
            .set('dateFrom', dateFrom)
            .set('dateTo', dateTo);

        return this.httpClient.post(url, {}, { params: params, responseType: 'blob' }).toPromise();
    }
}

@Injectable()
export class NbgCardUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'nbgCard' }), httpClient);
    }

    details(traderBankAccountId: number, resourceId: string): Promise<any> {
        const url = `${this.baseUrl}api/nbgCard/details`;

        const params = new HttpParams()
            .set('traderBankAccountId', traderBankAccountId)
            .set('resourceId', resourceId);

        return this.httpClient.post(url, {}, { params: params }).toPromise();
    }

    transactions(traderBankAccountId: number, resourceId: string, cardNumber: string, dateFrom: string, dateTo: string): Promise<Blob> {
        const url = `${this.baseUrl}api/nbgCard/transactions`;

        const params = new HttpParams()
            .set('traderBankAccountId', traderBankAccountId)
            .set('resourceId', resourceId)
            .set('cardNumber', cardNumber || '')
            .set('dateFrom', dateFrom)
            .set('dateTo', dateTo);

        return this.httpClient.post(url, {}, { params: params, responseType: 'blob' }).toPromise();
    }
}

@Injectable()
export class UserConnectionBankUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'userConnectionBank' }), httpClient);
    }


    getConfig(parentId: number): Promise<any> {
        const url = `${this.baseUrl}api/userConnectionBank/config`;

        const params = new HttpParams()
            .set('parentId', parentId);

        return this.httpClient.get(url, { params: params }).toPromise();
    }
}

@Injectable()
export class AvailableBankUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'availableBank' }), httpClient);
    }

}

@Injectable()
export class AccountListUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'accountList' }), httpClient);
    }

}

@Injectable()
export class CardListItemUnitOfWork extends UnitOfWork {

    constructor(
        @Inject('BASE_URL') public baseUrl: string,
        public httpClient: HttpClient) {
        super();
        this.repository = this.createRepository(new EntityResources({ baseUrl: baseUrl, controller: 'cardListItem' }), httpClient);
    }

}
